import { useEffect, useState } from "react";
import { Alert, Box, Button, Paper, Stack, Typography, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { CollectorCtx } from "../../pages/CollectorDetail";
import { listOnboardingPending, downloadContractPdf } from "../../services/collectors";
import { getContractStatus, ContractStatus } from "../../services/contracts";
import { StatusChip } from "./chips";
import ContractDialog from "./ContractDialog";

/**
 * Onboarding del collector. Lo que la API expone a un admin hoy:
 *  - GET /admin/v1/collectors/onboarding → ids con `onboarding = pending`
 *  - GET /app/v1/Onboarding/contract_pdf?collector_id → PDF del contrato
 *  - claves `onboarding`, `contract_*`, `data_*` en la config del collector
 *  - lambda `contracts` de webadmin2: estado del contrato y generación del que
 *    falta (colectores dados de alta sin onboarding, CMF los pide)
 * Los documentos y el formulario (`get_data`, `document`) exigen el par
 * web_key/secret_key del onboarding, que no está disponible para el admin.
 */
export default function OnboardingTab({ ctx }: { ctx: CollectorCtx }) {
  const { collector, config, notify, reload } = ctx;
  const [pending, setPending] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [contract, setContract] = useState<ContractStatus | null>(null);
  const [contractErr, setContractErr] = useState<string | null>(null);
  const [genOpen, setGenOpen] = useState(false);

  async function loadContract() {
    setContractErr(null);
    try { setContract(await getContractStatus(collector.id)); }
    catch (e: any) { setContractErr(String(e?.message ?? e)); }
  }
  useEffect(() => { void loadContract(); }, [collector.id]);

  useEffect(() => {
    let alive = true;
    listOnboardingPending()
      .then((ids) => alive && setPending(ids.some((i) => i.toLowerCase() === collector.id.toLowerCase())))
      .catch(() => alive && setPending(null));
    return () => { alive = false; };
  }, [collector.id]);

  const status = config.onboarding || (pending ? "pending" : "");
  const kind = /pending/i.test(status) ? "warn" : /done|complete|approved/i.test(status) ? "ok" : "neutral";

  async function downloadPdf() {
    setBusy(true);
    try { await downloadContractPdf(collector.id); }
    catch (e: any) { notify(String(e?.message ?? e), "error"); }
    finally { setBusy(false); }
  }

  const rows: [string, string | undefined][] = [
    ["Legal name", config.company_name],
    ["Tax type", config.data_collector_tax_type],
    ["Address", [config.data_address_street, config.data_address_street_number, config.data_address_floor, config.data_address_office].filter(Boolean).join(" ") || undefined],
    ["City / state / postal code", [config.data_address_city, config.data_address_state, config.data_address_postal_code].filter(Boolean).join(", ") || undefined],
    ["Representative", config.data_power_name ? `${config.data_power_name} ${config.data_power_lastname ?? ""} · ID ${config.data_power_id ?? "—"}` : undefined],
    ["Contract template", config.contract_version],
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography sx={{ fontWeight: 700 }}>Onboarding status</Typography>
        {pending === null && !status ? <CircularProgress size={16} /> : <StatusChip kind={kind} label={status || "no onboarding record"} />}
      </Stack>
      {kind === "warn" && (
        <Alert severity="info">
          <b>Pending ops review.</b> The collector completed the self-service form. Check the data below against Config, download the signed contract and, when everything is right, set <code>onboarding</code> to <code>done</code> in Config.
        </Alert>
      )}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Submitted data (from Config)</Typography>
          <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "6px 16px", m: 0, fontSize: 13.5, "& dt": { color: "text.secondary" }, "& dd": { m: 0, wordBreak: "break-word" } }}>
            {rows.map(([k, v]) => <Box key={k} sx={{ display: "contents" }}><dt>{k}</dt><dd>{v || <span style={{ color: "#888" }}>—</span>}</dd></Box>)}
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Contract &amp; documents</Typography>
            {contract
              ? <StatusChip kind={contract.has_contract ? "ok" : "warn"} label={contract.has_contract ? "Has contract" : "No contract"} />
              : !contractErr && <CircularProgress size={14} />}
          </Stack>
          {contractErr && <Alert severity="error" sx={{ mb: 1.5 }}>{contractErr}</Alert>}
          {contract?.has_contract && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Template <code>{contract.contract_version ?? "—"}</code>
              {contract.generated_by
                ? <> · generated from webadmin by {contract.generated_by} on {new Date(contract.generated_at ?? "").toLocaleString("es-AR")}</>
                : <> · from the vendor-portal onboarding</>}
            </Typography>
          )}
          {contract && !contract.has_contract && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {contract.can_generate
                ? <>This collector has no service contract (it was probably created from webadmin, without onboarding). Generate it with the same template the onboarding uses.</>
                : <>This collector has no service contract, and there's no contract template for <b>{contract.payments_processor || "its processor"}</b> (only Argentina).</>}
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
              onClick={downloadPdf} disabled={busy || !contract?.has_contract}>
              Signed contract (PDF)
            </Button>
            {contract?.can_generate && (
              <Button variant="contained" size="small" startIcon={<NoteAddOutlinedIcon />} onClick={() => setGenOpen(true)}>
                Generate contract
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", gap: 0.5, alignItems: "flex-start", mt: 2 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, mt: 0.2 }} />
            <span>Identity documents (ID, power of attorney, association memo) are only downloadable with the onboarding web/secret key pair, which the API doesn't expose to admins. Needs a backend change to list them here.</span>
          </Typography>
        </Paper>
      </Stack>
      {contract && (
        <ContractDialog open={genOpen} collectorId={collector.id} prefill={contract.prefill}
          onClose={() => setGenOpen(false)}
          onCreated={() => {
            setGenOpen(false);
            notify("Contract generated. You can download the PDF now.", "success");
            void loadContract(); void reload();
          }} />
      )}
    </Stack>
  );
}
